import React, { ReactElement, useEffect, useState } from "react";
import PlotlyChart from "react-plotlyjs-ts";

import { GraphType } from "../../page/Home";
import { unit } from "../../utils/utils";

interface Props {
  graphs: GraphType[];
  isShowedAll: boolean;
  yValue: string | null;
  maxX: number;
  isPercent: boolean;
}

const config = (
  graphs: GraphType[],
  maxX: number,
  isShowedAll: boolean,
  yValue: string | null,
  isPercent: boolean
): any => {
  let result: any = {};
  let minY = 100000;
  let maxY = 0;
  graphs.forEach((e) => {
    let tmpMinY = Math.min(...e.y);
    let tmpMaxY = Math.max(...e.y);
    if (minY > tmpMinY) {
      minY = tmpMinY;
    }
    if (tmpMaxY > maxY) {
      maxY = tmpMaxY;
    }
  });
  graphs.forEach((e, i) => {
    result[`yaxis${i + 1}`] = {
      range: [minY, maxY],
      title: {
        text:
          yValue && yValue in unit
            ? `${unit[yValue].name}[${isPercent ? "%" : unit[yValue].unit}]`
            : yValue,
      },
      zeroline: false,
    };
    result[`xaxis${i + 1}`] = {
      range: [0, maxX],
      showline: true,
      title: { text: "Cycles", standoff: 1 },
    };
  });
  if (!isShowedAll) {
    result["annotations"] = graphs
      .map((g) => g.idx)
      .map((e) => ({
        text: `DUT${e}`,
        font: {
          size: 12,
          color: e < 10 ? "blue" : e < 20 ? "green" : "red",
        },
        showarrow: false,
        align: "left",
        x:
          e % 10 < 3
            ? (((e - 1) % 10) * 1) / 6 + 0.05
            : e % 10 < 6
            ? ((e - 1) % 10) * 0.176 + 0.05
            : ((e - 1) % 10) * 0.178 + 0.04,
        y: e < 10 ? 1.02 : e < 20 ? 0.65 : 0.26,
        xref: "paper",
        yref: "paper",
      }));
  }
  return result;
};

const getRect = (
  y0: number,
  y1: number,
  color: string,
  x0 = -0.1,
  x1 = 1.02
): any => {
  return {
    type: "rect",
    xref: "paper",
    yref: "paper",
    opacity: 0.2,
    x0: x0,
    y0: y0,
    x1: x1,
    y1: y1,
    fillcolor: color,
    line: { width: 0.1 },
  };
};

const getRects = (y0: number, y1: number, color: string) => {
  return [
    getRect(y0, y1, color, -0.03, 0.137),
    getRect(y0, y1, color, 0.14, 0.31),
    getRect(y0, y1, color, 0.313, 0.483),
    getRect(y0, y1, color, 0.486, 0.655),
    getRect(y0, y1, color, 0.658, 0.828),
    getRect(y0, y1, color, 0.831, 1),
  ];
};

const getShape = (isShowedAll: boolean): any => {
  if (isShowedAll) {
    return {
      shapes: [],
    };
  }
  return {
    shapes: [
      ...getRects(-0.05, 0.285, "rgba(128, 0, 128, 0.7)"),
      ...getRects(0.32, 0.66, "rgba(158, 207, 155, 0.7)"),
      ...getRects(0.69, 1.02, "rgba(60, 97, 153, 0.7)"),
    ],
  };
};

export default function MainGraph({
  yValue,
  graphs,
  isShowedAll,
  maxX,
  isPercent,
}: Props): ReactElement {
  const [height, setHeight] = useState(window.innerHeight - 42);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight - 42);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  return (
    <PlotlyChart
      data={graphs}
      layout={{
        showlegend: isShowedAll ? true : false,
        title: {
          text:
            yValue && yValue in unit
              ? `<b>${unit[yValue].name}[${
                  isPercent ? "%" : unit[yValue].unit
                }]</b> to Cycle ${maxX}`
              : yValue,
          font: {
            family: "Oswald",
            size: 25,
          },
          xanchor: "center",
        },
        height: height,
        ...config(graphs, maxX, isShowedAll, yValue, isPercent),
        ...getShape(isShowedAll),
        grid: {
          columns: isShowedAll ? 1 : 6,
          rows: isShowedAll ? 1 : 3,
          pattern: "independent",
          roworder: "top to bottom",
        },
        paper_bgcolor: isShowedAll ? "#eee" : "#ffffff",
        legend: { font: { size: 18 } },
        margin: {
          l: 50,
          r: 3,
          b: 50,
          t: 50,
        },
      }}
    />
  );
}
